import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Users, Calendar, DollarSign, ArrowRight, ClipboardList } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, icon: Icon, color, testId }) => (
  <div className="stat-card" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <p className="stat-label">{label}</p>
        <p className={`stat-value ${color}`}>{value}</p>
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color.replace('text-', 'bg-').replace('600', '100')}`}>
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const [stats, setStats] = useState({ total_patients: 0, total_encounters: 0, total_revenue: 0 });
  const [recentPatients, setRecentPatients] = useState([]);
  const [encounterSummary, setEncounterSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { canAccessBilling, canAccessClinical, isReception } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/patients`)
      ];
      
      // Reception gets encounter summaries (limited view)
      if (isReception) {
        requests.push(axios.get(`${API}/encounters-summary`));
      }
      
      const responses = await Promise.all(requests);
      setStats(responses[0].data);
      setRecentPatients(responses[1].data.slice(0, 5));
      
      if (isReception && responses[2]) {
        setEncounterSummary(responses[2].data.slice(0, 5));
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <Layout>
        <div className="page-container flex items-center justify-center min-h-[50vh]">
          <div className="text-slate-500">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container" data-testid="dashboard-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Dashboard</h1>
            <p className="text-slate-500 mt-1">Welcome to My Med Pal</p>
          </div>
        </div>

        {/* Stats Grid - Dynamic based on role */}
        <div className={`grid grid-cols-1 ${canAccessBilling ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6 mb-8`}>
          <StatCard
            label="Total Patients"
            value={stats.total_patients}
            icon={Users}
            color="text-blue-600"
            testId="stat-patients"
          />
          <StatCard
            label="Total Encounters"
            value={stats.total_encounters}
            icon={Calendar}
            color="text-emerald-600"
            testId="stat-encounters"
          />
          {/* Revenue stat - only for owner */}
          {canAccessBilling && (
            <StatCard
              label="Total Revenue"
              value={formatCurrency(stats.total_revenue)}
              icon={DollarSign}
              color="text-violet-600"
              testId="stat-revenue"
            />
          )}
        </div>

        {/* Recent Patients & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Patients */}
          <div className="lg:col-span-2 card" data-testid="recent-patients">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900 font-heading">Recent Patients</h2>
                <button
                  onClick={() => navigate('/patients')}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                  data-testid="view-all-patients-btn"
                >
                  View all <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
            {recentPatients.length > 0 ? (
              <div>
                {recentPatients.map((patient) => (
                  <div
                    key={patient.id}
                    className="patient-row"
                    onClick={() => navigate(`/patients/${patient.id}`)}
                    data-testid={`patient-row-${patient.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="patient-avatar">
                        {patient.full_name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{patient.full_name}</p>
                        <p className="text-sm text-slate-500">{patient.phone || patient.email || 'No contact'}</p>
                      </div>
                    </div>
                    <span className="text-sm text-slate-400">{patient.gender}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <Users className="empty-state-icon" />
                <p className="empty-state-title">No patients yet</p>
                <p className="empty-state-text">Add your first patient to get started</p>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="card" data-testid="quick-actions">
            <div className="p-6 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900 font-heading">Quick Actions</h2>
            </div>
            <div className="p-6 space-y-3">
              <button
                onClick={() => navigate('/patients?action=new')}
                className="w-full btn-primary flex items-center justify-center gap-2"
                data-testid="quick-add-patient"
              >
                <Users className="w-4 h-4" />
                Add New Patient
              </button>
              {/* Encounter button - only for clinical staff */}
              {canAccessClinical && (
                <button
                  onClick={() => navigate('/encounters?action=new')}
                  className="w-full btn-secondary flex items-center justify-center gap-2"
                  data-testid="quick-add-encounter"
                >
                  <Calendar className="w-4 h-4" />
                  New Encounter
                </button>
              )}
              {/* Invoice button - only for owner */}
              {canAccessBilling && (
                <button
                  onClick={() => navigate('/billing?action=new')}
                  className="w-full btn-secondary flex items-center justify-center gap-2"
                  data-testid="quick-add-invoice"
                >
                  <DollarSign className="w-4 h-4" />
                  Create Invoice
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Reception-specific: Recent Encounter Summaries */}
        {isReception && encounterSummary.length > 0 && (
          <div className="mt-6 card" data-testid="encounter-summary-list">
            <div className="p-6 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900 font-heading">Recent Appointments</h2>
            </div>
            <div>
              {encounterSummary.map((enc) => (
                <div
                  key={enc.id}
                  className="p-4 border-b border-slate-100 last:border-b-0 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <ClipboardList className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{enc.patient_name}</p>
                      <p className="text-sm text-slate-500">{new Date(enc.date).toLocaleDateString()}</p>
                    </div>
                  </div>
                  {enc.signed_at && (
                    <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">Signed</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
